import { Button, Popover, message } from "antd";
import React, { useEffect, useState } from "react";
import AuthLayout from "../AuthLayout/AuthLayout";
import { ErrorMessage, Field, Formik, Form as FormikForm } from "formik";
import InputField from "../../components/Input/Inputfeild";
import EyeOnIcon from "../../assets/icons/eye-on.svg";
import EyeOffIcon from "../../assets/icons/eye-off.svg";
import "./CreateNewPwd.css";
import * as Yup from "yup";
import { Redirect, useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import PwdSuccess from "../../assets/Images/success-bg.png";
import { resetPasswordWithOtp } from "../../api/orgRegistry";
import { readPasswordResetFlow, canResetPassword, clearPasswordResetSession } from "../../utils/passwordResetSession";

// how long the success screen shows before it takes the user to Login on its own
const SUCCESS_REDIRECT_MS = 3000;

const CreateNewPwd = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [liveValidation, setLiveValidation] = useState({
    confirmPassword: null, // null: not validated, true: match, false: no match
  });

  const [pwdSuccess, setPwdSuccess] = useState(false);

  /* Router state on normal navigation, the session store on refresh. Read once so the
     values cannot change mid-submit - and so clearing the store on success does not
     yank this screen out from under the success message. */
  const [flow] = useState(() => readPasswordResetFlow(location.state));

  const initialValues = {
    newpassword: "",
    confirmPassword: "",
  };

  // the rules the popover promises, enforced rather than just displayed
  const validationSchema = Yup.object({
    newpassword: Yup.string()
      .required("Please input your new password!")
      .min(7, "Password must be at least 7 characters")
      .matches(/[A-Z]/, "Password must include an uppercase letter")
      .matches(/[a-z]/, "Password must include a lowercase letter")
      .matches(/\d/, "Password must include a number")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must include a special character"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newpassword"), null], "Passwords must match")
      .required("Please confirm your new password!"),
  });

  const handleIconClick = () => {
    setShowPassword((prev) => !prev);
  };

  // once the password is set there is nothing left to come back to
  useEffect(() => {
    if (!pwdSuccess) return;
    const timer = setTimeout(() => history.replace("/login"), SUCCESS_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [pwdSuccess, history]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await resetPasswordWithOtp({
        requestId: flow.otpRequestId,
        otp: flow.otp,
        email: flow.email,
        password: values.newpassword,
      });

      // the code is spent either way, so nothing is left behind for a Back button
      clearPasswordResetSession();
      setSubmitting(false);
      setPwdSuccess(true);
    } catch (error) {
      console.error("Password reset failed:", error);
      /* A failed reset burns the request id and the OTP with it, so there is nothing
         here worth retrying - the service needs a fresh code, which means starting at
         step 1 again. */
      clearPasswordResetSession();
      message.error(error.message || "Password reset failed. Please request a new verification code.");
      // settled before navigating away, so Formik is not written to after unmount
      setSubmitting(false);
      history.replace("/forget-password");
    }
  };

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 7) score += 20;
    if (/[A-Z]/.test(password)) score += 10;
    if (/[a-z]/.test(password)) score += 10;
    if (/\d/.test(password)) score += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 20;
    if (score === 80 && password.length > 10) score += 20;
    return score;
  };

  const getProgressBarColor = (score) => {
    if (score === 0) return "bg-transparent";
    if (score < 40) return "bg-red";
    if (score < 80) return "bg-yellow";
    return "bg-green";
  };

  const getStrengthText = (score) => {
    if (score < 40) return "This password is too weak. Try something stronger!";
    if (score < 80) return "Not bad, but your password could be more secure!";
    return "Your password is great. Nice work!";
  };

  const PasswordStrengthBar = ({ password }) => {
    const strength = getPasswordStrength(password);
    const colorClass = getProgressBarColor(strength);
    const strengthText = getStrengthText(strength);

    return (
      <div className="progress-bar-container">
        <div className={`progress-bar ${colorClass}`} style={{ width: `${strength}%` }}></div>
        <p className="progress-bar-text">{strengthText}</p>
      </div>
    );
  };

  const updatePasswordRulesColors = (password) => {
    const rules = {
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      minLength: password.length >= 7,
    };

    // the popover owns these nodes, so they only exist while it is open
    const paint = (id, passed) => {
      const el = document.getElementById(id);
      if (el) el.style.color = passed ? "green" : "red";
    };

    paint("uppercase", rules.uppercase);
    paint("lowercase", rules.lowercase);
    paint("number", rules.number);
    paint("special-character", rules.specialChar);
    paint("total-number", rules.minLength);
  };

  const handleConfirmPasswordIconClick = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Live validation for confirm password
  const validateConfirmPassword = (confirmPassword, newPassword) => {
    if (!confirmPassword) {
      setLiveValidation({ confirmPassword: null });
      return;
    }

    if (confirmPassword === newPassword) {
      setLiveValidation({ confirmPassword: true });
    } else {
      setLiveValidation({ confirmPassword: false });
    }
  };

  // Live validation message component
  const LiveValidationMessage = ({ field, validation }) => {
    if (validation === null) return null;

    return (
      <div className={`live-validation-message ${validation ? "success" : "error"}`}>
        {validation ? (
          <span style={{ color: "green" }}>✓ Passwords match</span>
        ) : (
          <span style={{ color: "red" }}>✗ Passwords do not match</span>
        )}
      </div>
    );
  };

  /* Reachable only with a verified code in hand. A direct URL, a Back into a spent
     step, or a refresh after the session was cleared all restart at step 1. Skipped
     once the reset has gone through, because clearing the session is what ends the
     flow and the success screen still has to render. */
  if (!pwdSuccess && !canResetPassword(flow)) {
    return <Redirect to="/forget-password" />;
  }

  return (
    <AuthLayout>
      <div className="signin-container">
        {!pwdSuccess ? (
          <>
            <div className="signin-header">
              <h1>Create New Password</h1>
              <p>Please enter a new password. Your new password must be different from previous password.</p>
            </div>

            <Formik
              validateOnChange={true}
              validateOnBlur={true}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => (
                <FormikForm className="login-form">
                  <Popover
                    open={isFocus}
                    id="popover-create-password"
                    content={
                      <div alignment="start">
                        <span className="eco-mb-20">The password must include</span>
                        <ul>
                          <li id="total-number">
                            <p className="pwd-strength-text">Minimum of 7 characters</p>
                          </li>
                          <li id="uppercase">
                            <p className="pwd-strength-text">At least one uppercase letter</p>
                          </li>
                          <li id="lowercase">
                            <p className="pwd-strength-text">At least one lowercase letter</p>
                          </li>
                          <li id="number">
                            <p className="pwd-strength-text">At least one number</p>
                          </li>
                          <li id="special-character">
                            <p className="pwd-strength-text">At least one special character</p>
                          </li>
                        </ul>
                      </div>
                    }
                    trigger="click"
                    placement="bottom"
                  >
                    <div className="input-wrapper">
                      <p>New Password</p>
                      <Field name="newpassword">
                        {({ field }) => (
                          <InputField
                            {...field}
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            icon={showPassword ? EyeOnIcon : EyeOffIcon}
                            iconClick={handleIconClick}
                            onChange={(e) => {
                              field.onChange(e);
                              updatePasswordRulesColors(e.target.value);
                              // Re-validate confirm password when new password changes
                              validateConfirmPassword(values.confirmPassword, e.target.value);
                            }}
                            onFocus={() => setIsFocus(true)}
                            onBlur={() => setIsFocus(false)}
                            value={values.newpassword}
                            className={`custom-input ${errors.newpassword && touched.newpassword ? "input-error" : ""}`}
                          />
                        )}
                      </Field>
                      <PasswordStrengthBar password={values.newpassword} />
                      <ErrorMessage name="newpassword" component="div" className="error-message" />
                    </div>
                  </Popover>

                  <div className="input-wrapper">
                    <p>Confirm Password</p>
                    <Field name="confirmPassword">
                      {({ field }) => (
                        <InputField
                          {...field}
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          icon={showConfirmPassword ? EyeOnIcon : EyeOffIcon}
                          iconClick={handleConfirmPasswordIconClick}
                          value={values.confirmPassword}
                          onChange={(e) => {
                            handleChange(e);
                            // Live validation on every keystroke
                            validateConfirmPassword(e.target.value, values.newpassword);
                          }}
                          className={`custom-input ${
                            errors.confirmPassword && touched.confirmPassword ? "input-error" : ""
                          } ${liveValidation.confirmPassword === false ? "input-error" : ""} ${
                            liveValidation.confirmPassword === true ? "input-success" : ""
                          }`}
                        />
                      )}
                    </Field>

                    {/* Live validation message */}
                    <LiveValidationMessage field="confirmPassword" validation={liveValidation.confirmPassword} />
                  </div>

                  <Button htmlType="submit" type="primary" className="signin-btn" disabled={isSubmitting}>
                    {isSubmitting ? "Resetting..." : "Reset Password"}
                  </Button>
                </FormikForm>
              )}
            </Formik>
          </>
        ) : (
          <div className="pwd-success-container">
            <img src={PwdSuccess} alt="Success" className="success-image" />
            <h1>You have successfully changed your password</h1>

            {/* was "/signin", which is not a route in this app - the button went nowhere */}
            <Button type="primary" className="signin-btn pwd-success-btn" onClick={() => history.replace("/login")}>
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default CreateNewPwd;
